import axios from 'axios';
import { ShipmentParams } from 'interfaces/Params.interface';
import { getIsDemoExperience } from 'services/globalConfig.services';
import { toShipmentsResponse } from 'services/demoShipment.services';
import type { DemoShipment } from 'interfaces/DemoShipment.interface';
import ENDPOINTS from '../constants/endpoints.constants';
import APIError from '../exceptions/APIError';

interface FetchShipmentsParams {
  organizationId: number;
  language: string;
  cmsSlug: string;
  scenario: string;
  searchTerms: ShipmentParams[];
  organizationSlug: string;
}

export interface FetchShipmentsReponse {
  data: {
    shipments: unknown[];
    token: string;
  };
}

/**
 * Backstop only. The bridge settles its own slot after 6 s with a marker, so
 * this budget is reached only by a bridge that is present but wedged — one that
 * answers `ptpDataResolver` without ever arming that timer. Ten seconds matches
 * the widget's own wait for its root element in `src/index.tsx`; a shorter one
 * could fire before the bridge's handshake and the parent's screen switch have
 * both landed.
 */
const DEMO_PAYLOAD_TIMEOUT_MS = 10000;

/**
 * The bridge resolves rather than rejects when nothing fills a slot. An
 * unchecked marker would be wrapped as a shipment and render an empty screen
 * with nothing in the console, which is the one failure a prospect cannot be
 * walked through.
 */
const isPayloadTimeout = (value: unknown): boolean =>
  !!value &&
  typeof value === 'object' &&
  (value as PerformPayloadTimeoutMarker).error === 'payload-timeout';

const awaitDemoShipments = async (): Promise<DemoShipment | DemoShipment[]> => {
  // The global's name is a cross-repo contract published by the bridge, not
  // ours to rename.
  // eslint-disable-next-line no-underscore-dangle
  const resolver = window.__performIframeService?.ptpDataResolver;

  if (!resolver) {
    throw new APIError('[PTP] demo bridge is not present');
  }

  let timer: ReturnType<typeof setTimeout>;
  const expiry = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new APIError('[PTP] demo payload did not arrive')),
      DEMO_PAYLOAD_TIMEOUT_MS,
    );
  });

  try {
    const payload = await Promise.race([resolver('shipment'), expiry]);

    if (isPayloadTimeout(payload)) {
      throw new APIError('[PTP] demo payload timed out at the bridge');
    }

    return payload as DemoShipment | DemoShipment[];
  } finally {
    clearTimeout(timer);
  }
};

export const fetchShipments = async (
  params: FetchShipmentsParams,
): Promise<FetchShipmentsReponse> => {
  if (getIsDemoExperience()) {
    // `params` is deliberately unused. The payload is whatever the parent sent
    // for this screen; there is nothing to search for, and no request may leave
    // the page. Returning here, before the request is built, is what makes that
    // true rather than merely ignoring a response.
    return toShipmentsResponse(await awaitDemoShipments());
  }

  try {
    const { organizationId, language, cmsSlug, scenario, organizationSlug } =
      params;

    // check params.length > 20 => encrypt value => replace space to +
    // TODO: raise BE encode encrypt params
    const searchTerms = params.searchTerms.map((item) => ({
      ...item,
      value:
        item.value.length > 20 ? item.value.replace(/\s/g, '+') : item.value,
    }));

    const { data } = await axios.post(
      `${ENDPOINTS.TRACKING_ANTENNA_HOST}/api/v1/ptp/shipments/${organizationId}`,
      {
        scenario,
        slug: cmsSlug,
        lang: language,
        search_terms: searchTerms,
        organization_slug: organizationSlug,
      },
    );

    return data;
  } catch (error) {
    if (error.response) {
      throw new APIError(error.response.data?.message, error);
    }
    throw error;
  }
};
